import type { FieldError } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import type { PasswordStrength } from "@/react/helpers/passwordStrength";
import {
  getPasswordRequirementMessage,
  isGreatPassword,
} from "@/react/helpers/passwordStrength";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";

interface CheckoutFormPasswordStrengthIndicatorProps {
  passwordStrength: PasswordStrength;
  passwordFieldError?: FieldError;
  passwordValue?: string;
}

export const CheckoutFormPasswordStrengthIndicator = ({
  passwordStrength,
  passwordFieldError,
  passwordValue,
}: CheckoutFormPasswordStrengthIndicatorProps) => {
  const requirementMessage = getPasswordRequirementMessage(
    passwordStrength,
    !!passwordValue,
  );

  return (
    <div className="mt-4 space-y-1">
      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
        <div
          className={classNames(
            "h-2 rounded-full transition-all duration-300 ease-in-out",
            passwordStrength.progressBarColor,
          )}
          style={{ width: `${passwordStrength.percentage}%` }}
        />
      </div>
      {passwordStrength.strengthMessage && (
        <p className="text-sm font-medium text-neutral-900 dark:text-white">
          {passwordStrength.strengthMessage}
        </p>
      )}
      <p
        className={classNames(
          "flex items-center text-neutral-500 dark:text-neutral-400",
          isGreatPassword(passwordStrength) ? "text-xs" : "text-sm",
        )}
      >
        {isGreatPassword(passwordStrength) && !passwordFieldError && (
          <Icon
            type="20-checkmark"
            className="ml-0.5 mr-1.5 mt-0.5 shrink-0 text-green-600"
          />
        )}
        <span>{requirementMessage}</span>
      </p>
      {passwordFieldError && (
        <p className="flex items-center text-xs text-neutral-500 dark:text-neutral-400">
          <Icon
            type="16-close-v2"
            className="mx-0.5 mt-2 shrink-0 text-red-500"
          />
          <span>{t("paywalls.checkout.password_error.criteria_not_met")}</span>
        </p>
      )}
    </div>
  );
};
